import { DialogTitle } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../../context'
import { useSampleDataDialog } from '../../context/sample-data-context'
import { query } from '../../graphql'
import {
  SAMPLE_DATA_AVAILABILITY,
  getSampleDataErrorKey,
  isSampleSpace,
  isSampleTemplatePartner,
  partitionBySample
} from '../../helpers/sample-data'
import {
  useRemoveSampleData,
  useSampleDataAccess
} from '../../hooks/use-sample-data'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'

const TRANSLATION_LOCATIONS = ['components.career.talents.']
const SAMPLE = 'sample_data.delete_space.'

const DeleteSpace = ({ spaces, updateHandler, open, setOpen }) => {
  const { t } = useTranslation()
  const { is_trial } = usePaymentContext()
  const { success, error } = useNotificationContext()
  const { partner } = usePartnerContext()
  const {
    cognitoUser: { username }
  } = useUserContext()

  const queryClient = useQueryClient()
  const { openSampleDataDialog } = useSampleDataDialog()
  const { gate } = useSampleDataAccess()
  // Kein Reset, sonst rendert /talents den Spinner und hängt diesen Dialog
  // mitten im Lauf aus
  const { mutateAsync: removeSampleData } = useRemoveSampleData({
    resetTalents: false
  })

  const [isLoading, setIsLoading] = useState(false)
  const translation = TRANSLATION_LOCATIONS[0]

  // Echte Talente werden wie bisher freigegeben (Soft-Delete, 14 Tage
  // wiederherstellbar), Beispieltalente hart über removeSampleData entfernt.
  const { sample, real } = partitionBySample(spaces, isSampleSpace)
  // Im Discover-Onboarding werden die Beispieldaten noch gebraucht, geschützte
  // Partner lehnen removeSampleData ab. Ohne Löschrecht entscheidet das
  // Backend, wie bei echten Talenten auch.
  const isSampleLocked =
    gate === SAMPLE_DATA_AVAILABILITY.ONBOARDING ||
    gate === SAMPLE_DATA_AVAILABILITY.TEMPLATE_PARTNER
  const lockedKey =
    gate === SAMPLE_DATA_AVAILABILITY.TEMPLATE_PARTNER
      ? isSampleTemplatePartner(partner.id)
        ? 'sample_data.info.template'
        : 'sample_data.info.protected'
      : 'sample_data.info.onboarding'
  const removableSample = isSampleLocked ? [] : sample
  const onlySample = sample.length > 0 && real.length === 0

  const openRemoveAll = () => {
    setOpen(false)
    // erst schließen, dann öffnen (Muster talents.js)
    setTimeout(
      () => openSampleDataDialog({ mode: 'remove', source: 'delete_space' }),
      0.3 * 1000
    )
  }

  const deleteSpace = async () => {
    setIsLoading(true)

    const softDeleteSpace = ({ space_id }) => {
      return query({
        query: 'spaceControl',
        variables: { action: 'softDeleteSpace', space_id, user_id: username }
      })
    }

    try {
      await Promise.all(real.map(({ id }) => softDeleteSpace({ space_id: id })))
    } catch {
      setIsLoading(false)
      return
    }

    let sampleFailed = false

    if (removableSample.length) {
      try {
        // frischt die Talent-Caches selbst auf
        await removeSampleData({
          scope: { space_ids: removableSample.map(({ id }) => id) },
          source: 'delete_space'
        })
      } catch (err) {
        sampleFailed = true
        const errorKey = getSampleDataErrorKey(err)
        if (errorKey) error(t(errorKey))
      }
    }

    // die ganze Familie QUERY_KEYS.talentsByPartner, nicht nur der eigene
    // Username, dazu Discover-Picker und gelöschte Talente
    const invalidations = [
      queryClient.invalidateQueries({ queryKey: ['talents', partner.id] }),
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.spacesByPartner(partner.id)
      }),
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.recoverTalentsByPartner(partner.id)
      })
    ]

    if (is_trial) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.trialDataByPartner(partner.id)
        })
      )
    }

    await Promise.all(invalidations)

    setIsLoading(false)

    // Schlägt nur das Entfernen der Beispieltalente fehl, sind die echten
    // schon freigegeben. Dann bleibt der Dialog für einen neuen Versuch offen,
    // falls es keine echten gab.
    if (sampleFailed && !real.length) return

    updateHandler()

    if (!sampleFailed) {
      success(
        onlySample && !isSampleLocked
          ? t('sample_data.remove.success')
          : t(translation + 'delete_space_success')
      )
    }
    setOpen(false)
  }

  const renderContent = () => {
    if (onlySample) {
      return (
        <>
          <DialogTitle className='text-center text-2xl font-medium leading-6 text-gray-900'>
            {t(SAMPLE + 'only_title', { count: sample.length })}
          </DialogTitle>
          <span className='mt-2 block text-center text-base text-gray-600'>
            {isSampleLocked ? t(lockedKey) : t(SAMPLE + 'only_text')}
          </span>
        </>
      )
    }

    return (
      <>
        <DialogTitle className='text-center text-2xl font-medium leading-6 text-gray-900'>
          {t(translation + 'modal_delete_space_title')}
        </DialogTitle>
        <span className='mt-2 block text-center text-base text-gray-600'>
          {t(translation + 'modal_delete_space_description')}
        </span>
        {sample.length > 0 && (
          <p className='mt-3 text-center text-sm text-gray-600'>
            {isSampleLocked
              ? t(lockedKey)
              : t(SAMPLE + 'mixed_hint', { count: sample.length })}
          </p>
        )}
      </>
    )
  }

  const confirmText = () => {
    if (onlySample) {
      return t(SAMPLE + 'only_confirm', { count: sample.length })
    }

    // gesperrte Beispieltalente bleiben, gezählt wird, was freigegeben wird
    const count = real.length + removableSample.length

    return count > 1
      ? t(translation + 'modal_delete_space_submit_action_multiple', { count })
      : t(translation + 'modal_delete_space_submit_action_single', { count })
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='lg'>
      {renderContent()}
      {sample.length > 0 && gate === null && (
        <div className='mt-3 text-center'>
          <button
            type='button'
            onClick={openRemoveAll}
            className='text-sm font-semibold text-[#1660F3] hover:text-primaryBlue/70'
          >
            {t(SAMPLE + 'remove_all_link')}
          </button>
        </div>
      )}
      {/* unter sm gestapelt, sonst laufen lange Labels über */}
      <div className='mt-8 flex flex-col-reverse gap-3 sm:flex-row'>
        <Button.SecondaryLG
          fullWidth
          text={t(translation + 'modal_delete_space_cancel_action')}
          onClick={() => setOpen(false)}
        />
        <Button.WarningLG
          fullWidth
          text={confirmText()}
          onClick={deleteSpace}
          isLoading={isLoading}
          disabled={onlySample && isSampleLocked}
        />
      </div>
    </Modal>
  )
}

DeleteSpace.propTypes = {
  spaces: PropTypes.array.isRequired,
  updateHandler: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

DeleteSpace.defaultProps = {
  updateHandler: () => undefined
}

export default DeleteSpace
